import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import KendoActionsCell from '@components/Kendo/ActionsCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import { Button } from '@ui';
import { timeEntryPath } from 'routes';
import { TimeEntriesContext } from '../TimeEntriesContext';
import redirectPath from '../helpers/redirectPath';

const namespace = 'features.time_entries.index.table.actions';

function ActionsCell({ dataItem, setModalState, colSpan, className, ariaColumnIndex, style }) {
  const { screen, screenId } = useContext(TimeEntriesContext);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

  const authToken = useMemo(() => document.querySelector('meta[name="csrf-token"]').getAttribute('content'), []);

  const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);

  const actionsList = useMemo(() => {
    const list = [];

    list.push({
      title: translate('view', { namespace }),
      faClass: 'fa-eye',
      onClick: () => setModalState({ visible: true, timeEntryId: dataItem.id }),
    });

    list.push({
      title: translate('delete', { namespace }),
      faClass: 'fa-trash-can',
      onClick: toggleDeleteDialog,
    });

    return list;
  }, [dataItem.id, toggleDeleteDialog, setModalState]);

  const deletePath = useMemo(
    () => timeEntryPath({ id: dataItem.id, redirect_path: redirectPath({ screen, screenId }), _options: true }),
    [dataItem.id, screen, screenId]
  );

  return (
    <>
      <KendoActionsCell
        colSpan={colSpan}
        className={className}
        ariaColumnIndex={ariaColumnIndex}
        style={style}
        actionsList={actionsList}
      />

      {deleteDialogVisible && (
        <Dialog title={I18n.t('generic.confirm')} className="qmb-dialog" onClose={toggleDeleteDialog}>
          <p>{translate('confirm_delete', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <form action={deletePath} method="POST">
              <input type="hidden" value="delete" name="_method" />
              <input name="authenticity_token" type="hidden" value={authToken} />
              <Button type="button" onClick={toggleDeleteDialog}>{I18n.t('generic.cancel')}</Button>
              <Button type="submit" variant="primary">
                {I18n.t('generic.ok')}
              </Button>
            </form>
          </div>
        </Dialog>
      )}
    </>
  );
}

ActionsCell.propTypes = {
  ariaColumnIndex: PropTypes.number.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  colSpan: PropTypes.number.isRequired,
  dataItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    canDelete: PropTypes.bool
  }).isRequired,
  setModalState: PropTypes.func.isRequired
};

ActionsCell.defaultProps = {
  style: null,
  className: ''
};

export default ActionsCell;
